import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  acknowledgeLostImageNotice,
  getLostImageNotices,
  LOST_IMAGES_EVENT,
  reasonOf,
  type LostImageNotice,
} from './lostImageNotices';
import { ActionButton, Message, Snackbar, StatusDot } from './offlineBannerStyles';

/**
 * Aviso fijo de imágenes perdidas de la bandeja (spec
 * 2026-10-01-photo-outbox-identity § 5.4, decisión del usuario del 1-oct).
 *
 * Dice de qué orden era la imagen y qué hacer («Vuelve a añadirla»). No tiene
 * aspa: solo se va con «Entendido», y como los avisos viven en localforage,
 * sobrevive a recargas. Si hay varios, enseña el más antiguo; al pulsar
 * «Entendido» aparece el siguiente.
 */
export function LostImagesNotice() {
  const { t } = useTranslation('common');
  const [notices, setNotices] = useState<LostImageNotice[]>([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const current = await getLostImageNotices();
      if (alive) setNotices(current);
    };
    void load();
    window.addEventListener(LOST_IMAGES_EVENT, load);
    return () => {
      alive = false;
      window.removeEventListener(LOST_IMAGES_EVENT, load);
    };
  }, []);

  const oldest = [...notices].sort((a, b) => a.lastAt - b.lastAt)[0];
  if (!oldest) return null;

  // «Vuelve a añadirla» solo tiene sentido si la orden sigue ahí; si se borró,
  // el aviso dice eso y nada más (spec 2026-10-12-image-order-gone).
  const reason = reasonOf(oldest);
  const message =
    reason === 'order-gone'
      ? oldest.kind === 'sketch'
        ? t('offline.orderGoneSketch', { roid: oldest.roid })
        : t('offline.orderGonePhotos', { count: oldest.count, roid: oldest.roid })
      : oldest.kind === 'sketch'
        ? t('offline.lostSketch', { roid: oldest.roid })
        : t('offline.lostPhotos', { count: oldest.count, roid: oldest.roid });

  return (
    <Snackbar $variant="failed" $exiting={false} role="alert">
      <StatusDot $variant="failed" />
      <Message>{message}</Message>
      <ActionButton
        onClick={() => void acknowledgeLostImageNotice(oldest.orderId, oldest.kind, reason)}
      >
        {t('offline.understood')}
      </ActionButton>
    </Snackbar>
  );
}
